import React, { useEffect, useRef, useState } from "react";
import { Conversation } from "..";
import { ChatSidebarContent } from "./ChatSideBarContent";
import { Agent } from "types";
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from "@chakra-ui/modal";

interface ChatSidebarProps {
  conversations: Conversation[];
  availableAgents: Agent[];
  canContinue: boolean;
  isLoadingConversations: boolean;
  isLoadingAgents: boolean;
  onConversationClick: (conversationId: string, agentId: string) => void;
  onLoadMore: () => void;
  selectedAgentId?: string;
  onAgentSelect: (agentId: string) => void;
  conversationsError: string | null;
  agentsError: string | null;
  onRetryConversations: () => void;
  onRetryAgents: () => void;
  open: boolean;
  setOpen: (b: boolean) => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  availableAgents,
  isLoadingConversations,
  onConversationClick,
  onLoadMore,
  canContinue,
  selectedAgentId,
  onAgentSelect,
  isLoadingAgents,
  conversationsError,
  agentsError,
  onRetryConversations,
  onRetryAgents,
  open,
  setOpen,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
        setOpen(false); // Auto-close Drawer on desktop
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setOpen]);

  useEffect(() => {
    if (!isMobile && open) setOpen(false);
  }, [isMobile, open, setOpen]);

  // Implement click outside for mobile drawer
  useEffect(() => {
    if (!(isMobile && open)) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerContentRef.current &&
        !drawerContentRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, open, setOpen]);

  return (
    <>
      {/* Sidebar for desktop */}
      <div
        className={`relative hidden transition-all duration-300 lg:static lg:block lg:h-auto lg:translate-x-0 lg:rounded-xl ${
          isCollapsed ? "lg:w-20" : "lg:w-[340px]"
        }`}
      >
        <ChatSidebarContent
          onAgentSelect={onAgentSelect}
          conversations={conversations}
          availableAgents={availableAgents}
          isLoadingConversations={isLoadingConversations}
          onConversationClick={onConversationClick}
          onLoadMore={onLoadMore}
          canContinue={canContinue}
          selectedAgentId={selectedAgentId}
          isLoadingAgents={isLoadingAgents}
          conversationsError={conversationsError}
          agentsError={agentsError}
          onRetryConversations={onRetryConversations}
          onRetryAgents={onRetryAgents}
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        />
      </div>
      {/* Drawer for mobile */}
      <Drawer
        isOpen={isMobile && open}
        onClose={() => setOpen(false)}
        placement="left"
      >
        <div
          className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
            `}
        />
        <DrawerContent
          ref={drawerContentRef}
          className="z-[999999] max-w-[50vw] rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-navy-800 lg:hidden"
        >
          <DrawerHeader
            px="28px"
            w={{ base: "100%", md: "400px" }}
            pt="24px"
            pb="0px"
          >
            <DrawerCloseButton className="absolute right-[26px] top-[16px] h-4 w-4 text-gray-900 dark:text-white" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              Agent chats
            </p>
          </DrawerHeader>
          <DrawerBody
            className={`
              flex
              min-h-screen flex-col
              bg-white
              p-0 dark:bg-navy-800
            `}
          >
            <div
              className={`
                flex h-full flex-col
                overflow-y-auto rounded-xl border-r
                border-gray-100 bg-white
                shadow-md
                dark:border-navy-700
                dark:bg-navy-800
              `}
            >
              <ChatSidebarContent
                onAgentSelect={onAgentSelect}
                conversations={conversations}
                availableAgents={availableAgents}
                isLoadingConversations={isLoadingConversations}
                onConversationClick={onConversationClick}
                onLoadMore={onLoadMore}
                canContinue={canContinue}
                selectedAgentId={selectedAgentId}
                isLoadingAgents={isLoadingAgents}
                conversationsError={conversationsError}
                agentsError={agentsError}
                onRetryConversations={onRetryConversations}
                onRetryAgents={onRetryAgents}
              />
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ChatSidebar;
